import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Loader2, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { RelatedBlogs } from './BlogIntegrated';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [pageSettings, setPageSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, settingsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/service-menu?active_only=true`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/services-page-settings`)
        ]);
        
        if (servicesRes.ok) {
          const data = await servicesRes.json();
          setServices(data);
        }
        
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          setPageSettings(settings);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-rotate hero images
  useEffect(() => {
    if (pageSettings?.hero_images?.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => 
          (prev + 1) % pageSettings.hero_images.length
        );
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [pageSettings]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-12 h-12 animate-spin text-[#2a2a2a]" />
      </div>
    );
  }

  const activeService = services[activeIndex];
  const heroImages = pageSettings?.hero_images?.length > 0 
    ? pageSettings.hero_images 
    : services.filter(s => s.hero_image).map(s => s.hero_image);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Our Services | Navaris Digital"
        description={pageSettings?.hero_subtitle || "Comprehensive technology solutions designed to accelerate your digital transformation"}
      />
      
      {/* Hero Section - Multi-image with CMS content */}
      <section className="relative min-h-[70vh] flex items-center bg-[#2a2a2a] overflow-hidden">
        {/* Background Images with Transition */}
        <div className="absolute inset-0">
          <AnimatePresence mode="wait">
            {heroImages.length > 0 && (
              <motion.img
                key={currentImageIndex}
                src={heroImages[currentImageIndex]}
                alt="Services"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 0.4, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2a2a2a] via-[#2a2a2a]/90 to-[#2a2a2a]/70" />
        </div>

        {/* Dot Pattern Overlay */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-white/50 text-sm mb-6"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight size={14} />
            <span className="text-white">Services</span>
          </motion.div>

          <div className="max-w-3xl">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.1]"
            >
              {pageSettings?.hero_title || "Services that drive growth"}
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-white/70 mb-10 leading-relaxed"
            >
              {pageSettings?.hero_subtitle || "We deliver end-to-end technology solutions that transform businesses and create lasting competitive advantage."}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex items-center gap-8"
            >
              <div>
                <div className="text-5xl font-bold text-white">{services.length}</div>
                <div className="text-white/50 text-sm mt-1">Core Services</div>
              </div>
              <div className="w-px h-16 bg-white/20" />
              <div>
                <div className="text-5xl font-bold text-white">
                  {services.reduce((acc, s) => acc + (s.sub_items?.length || 0), 0)}+
                </div>
                <div className="text-white/50 text-sm mt-1">Solutions</div>
              </div>
            </motion.div>

            {/* Image Indicators */}
            {heroImages.length > 1 && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex gap-2 mt-10"
              >
                {heroImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      idx === currentImageIndex ? 'w-8 bg-white' : 'w-4 bg-white/30 hover:bg-white/50'
                    }`}
                  />
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Interactive Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Left - Service Tabs */}
            <div className="lg:col-span-4">
              <p className="text-gray-400 text-xs uppercase tracking-widest mb-6 font-medium">
                Our Expertise
              </p>
              
              <div className="space-y-2">
                {services.map((service, index) => (
                  <motion.button
                    key={service.id}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => setActiveIndex(index)}
                    className={`w-full text-left p-4 rounded-xl transition-all duration-300 group ${
                      activeIndex === index
                        ? 'bg-[#2a2a2a] text-white shadow-lg'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-bold ${
                          activeIndex === index ? 'text-white/50' : 'text-gray-400'
                        }`}>
                          0{index + 1}
                        </span>
                        <span className="font-semibold">{service.name}</span>
                      </div>
                      <ArrowRight 
                        size={16} 
                        className={`transition-all ${
                          activeIndex === index ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
                        }`}
                      />
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Right - Active Service Details */}
            <div className="lg:col-span-8">
              <AnimatePresence mode="wait">
                {activeService && (
                  <motion.div
                    key={activeService.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Hero Image */}
                    <Link to={`/services/${activeService.slug}`} className="block group">
                      <div className="relative rounded-2xl overflow-hidden mb-6">
                        {activeService.hero_image ? (
                          <img 
                            src={activeService.hero_image} 
                            alt={activeService.name}
                            className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                        ) : (
                          <div className="w-full h-72 bg-gradient-to-br from-[#2a2a2a] to-gray-600" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#2a2a2a]/90 via-[#2a2a2a]/30 to-transparent" />
                        
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <h2 className="text-3xl font-bold text-white mb-2">{activeService.name}</h2>
                          <p className="text-white/70 line-clamp-2">{activeService.description}</p>
                        </div>

                        <div className="absolute top-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all">
                          <ArrowUpRight size={24} className="text-[#2a2a2a]" />
                        </div>
                      </div>
                    </Link>

                    {/* Sub-services Grid */}
                    {activeService.sub_items && activeService.sub_items.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {activeService.sub_items.slice(0, 8).map((sub, idx) => (
                          <Link
                            key={sub.id}
                            to={`/services/${activeService.slug}/${sub.slug}`}
                            className="p-4 bg-gray-50 rounded-xl hover:bg-[#2a2a2a] hover:text-white transition-all group text-center"
                          >
                            <span className="text-sm font-medium line-clamp-2">{sub.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links - Compact Cards */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-[#2a2a2a]">All Services</h2>
            <Link to="/contact" className="text-[#2a2a2a] hover:underline text-sm font-medium flex items-center gap-1">
              Need help choosing? <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className="block bg-white rounded-xl p-5 hover:shadow-lg transition-all group border border-gray-100 hover:border-[#2a2a2a]/20 h-full"
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-xs font-bold text-gray-300">0{index + 1}</span>
                    <ArrowUpRight size={16} className="text-gray-300 group-hover:text-[#2a2a2a] transition-colors" />
                  </div>
                  
                  <h3 className="font-bold text-[#2a2a2a] mb-2 group-hover:text-gray-600 transition-colors">
                    {service.name}
                  </h3>
                  
                  <p className="text-gray-500 text-sm line-clamp-2 mb-3">
                    {service.description}
                  </p>
                  
                  <div className="text-xs text-gray-400">
                    {service.sub_items?.length || 0} solutions
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2a2a2a]">
        <div className="container mx-auto px-4 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-white mb-4"
          >
            Ready to get started?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/70 text-lg mb-8 max-w-xl mx-auto"
          >
            Let's discuss how our services can help you achieve your business goals.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Button asChild size="lg" className="bg-white text-[#2a2a2a] hover:bg-gray-100 text-base px-8">
              <Link to="/contact">
                Contact Us <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Related Blog Posts */}
      {services.length > 0 && (
        <RelatedBlogs 
          serviceIds={services.map(s => s.id)} 
          title="Latest Insights"
          limit={3}
        />
      )}
    </div>
  );
};

// Keep the old exports for backward compatibility
const ServiceCategory = () => null;
const SubserviceDetail = () => null;

export { ServicesPage, ServiceCategory, SubserviceDetail };
